/** @jsx jsx */
import { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import DayPicker from "react-day-picker"
import { Flex, jsx } from "theme-ui"
import "react-day-picker/lib/style.css"

import { formatDate } from "../DepositTerms/helper"

const MONTHS = [
  "Januari",
  "Februari",
  "Mars",
  "April",
  "Maj",
  "Juni",
  "Juli",
  "Augusti",
  "September",
  "Oktober",
  "November",
  "December",
]
const WEEKDAYS_LONG = [
  "Söndag",
  "Måndag",
  "Tisdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lördag",
]
const WEEKDAYS_SHORT = ["Sö", "Må", "Ti", "On", "To", "Fr", "Lö"]

const parseDate = isoDate => {
  const [year, month, day] = isoDate.split("-").map(Number)
  return new Date(year, month - 1, day)
}

const monthsBefore = (date, months) =>
  new Date(date.getFullYear(), date.getMonth() - months, date.getDate())

const PRESETS = [
  { label: "3 mån", months: 3 },
  { label: "6 mån", months: 6 },
  { label: "1 år", months: 12 },
  { label: "All historik", months: null },
]

const pillStyles = {
  fontFamily: "body",
  fontSize: 1,
  color: "text",
  backgroundColor: "white",
  border: "2px solid",
  borderColor: "grey.light",
  borderRadius: "100px",
  cursor: "pointer",
  px: 6,
  py: 3,
  ":hover, :focus-visible": {
    borderColor: "black",
    outline: "none",
  },
}

export default function DateRangePicker({ from, to, min, max, onApply }) {
  const containerRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState({})
  const [numberOfMonths, setNumberOfMonths] = useState(1)
  const [initialMonth, setInitialMonth] = useState(null)

  const minDate = parseDate(min)
  const maxDate = parseDate(max)

  useEffect(() => {
    if (!isOpen) return undefined

    const closeOnOutsideClick = event => {
      if (!containerRef.current.contains(event.target)) setIsOpen(false)
    }
    const closeOnEscape = event => {
      if (event.key === "Escape") setIsOpen(false)
    }
    document.addEventListener("mousedown", closeOnOutsideClick)
    document.addEventListener("keydown", closeOnEscape)
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick)
      document.removeEventListener("keydown", closeOnEscape)
    }
  }, [isOpen])

  const open = () => {
    const isWide = window.matchMedia("(min-width: 768px)").matches
    setDraft({ from: parseDate(from), to: parseDate(to) })
    setNumberOfMonths(isWide ? 2 : 1)
    setInitialMonth(isWide ? monthsBefore(parseDate(to), 1) : parseDate(to))
    setIsOpen(true)
  }

  const apply = range => {
    onApply({ from: formatDate(range.from), to: formatDate(range.to) })
    setIsOpen(false)
  }

  const selectDay = (day, { disabled }) => {
    if (disabled) return
    const isStartingOver = !draft.from || draft.to || day < draft.from
    setDraft(isStartingOver ? { from: day } : { from: draft.from, to: day })
  }

  const applyPreset = ({ months }) => {
    const presetFrom = months ? monthsBefore(maxDate, months) : minDate
    apply({ from: presetFrom > minDate ? presetFrom : minDate, to: maxDate })
  }

  return (
    <div ref={containerRef} sx={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        sx={{ ...pillStyles, fontSize: 2, px: 7, py: 4 }}
      >
        <span sx={{ color: "grey.dark", mr: 3 }}>Period</span>
        {from} – {to}
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Välj period"
          sx={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            zIndex: 10,
            maxWidth: "100%",
            p: [5, 7],
            backgroundColor: "white",
            border: "2px solid",
            borderColor: "grey.light",
            borderRadius: "16px",
            boxShadow: "0 8px 24px rgba(19, 19, 19, 0.08)",
            ".DayPicker": {
              fontFamily: "body",
              fontSize: 1,
            },
            ".DayPicker-wrapper": {
              outline: "none",
              padding: 0,
            },
            ".DayPicker-Months": {
              flexWrap: "nowrap",
              gap: 8,
            },
            ".DayPicker-Month": {
              margin: 0,
            },
            ".DayPicker-Caption > div": {
              fontSize: 2,
              fontWeight: "bold",
            },
            ".DayPicker-NavButton": {
              top: 0,
              right: 0,
            },
            ".DayPicker-Weekday": {
              color: "grey.dark",
              fontSize: 0,
            },
            ".DayPicker-Day": {
              borderRadius: 0,
              padding: "2px 0",
            },
            ".DayPicker-Day .day-number": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              mx: "auto",
              borderRadius: "100px",
            },
            ".DayPicker-Day--today": {
              color: "text",
              fontWeight: "bold",
            },
            ".DayPicker-Day--disabled": {
              color: "disabled",
              cursor: "default",
            },
            ".DayPicker:not(.DayPicker--interactionDisabled) .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover": {
              backgroundColor: "transparent",
              ".day-number": {
                backgroundColor: "grey.lighter",
              },
            },
            ".DayPicker-Day--selected:not(.DayPicker-Day--outside)": {
              backgroundColor: "grey.lighter",
              color: "text",
            },
            ".DayPicker-Day--start:not(.DayPicker-Day--outside), .DayPicker-Day--end:not(.DayPicker-Day--outside)": {
              ".day-number": {
                backgroundColor: "black",
                color: "white",
              },
            },
          }}
        >
          <Flex sx={{ flexWrap: "wrap", gap: 3, mb: 5 }}>
            {PRESETS.map(preset => (
              <button
                key={preset.label}
                type="button"
                onClick={() => applyPreset(preset)}
                sx={pillStyles}
              >
                {preset.label}
              </button>
            ))}
          </Flex>

          <DayPicker
            locale="sv"
            months={MONTHS}
            weekdaysLong={WEEKDAYS_LONG}
            weekdaysShort={WEEKDAYS_SHORT}
            firstDayOfWeek={1}
            numberOfMonths={numberOfMonths}
            initialMonth={initialMonth}
            fromMonth={minDate}
            toMonth={maxDate}
            disabledDays={[{ before: minDate }, { after: maxDate }]}
            selectedDays={[draft.from, { from: draft.from, to: draft.to }]}
            modifiers={{ start: draft.from, end: draft.to }}
            onDayClick={selectDay}
            renderDay={day => (
              <span className="day-number">{day.getDate()}</span>
            )}
          />

          <Flex
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 5,
              mt: 5,
            }}
          >
            <span sx={{ fontSize: 1, color: "grey.dark" }}>
              {draft.to ? "" : "Välj slutdatum"}
            </span>
            <Flex sx={{ gap: 3 }}>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                sx={{ ...pillStyles, borderColor: "white" }}
              >
                Avbryt
              </button>
              <button
                type="button"
                disabled={!draft.from || !draft.to}
                onClick={() => apply(draft)}
                sx={{ variant: "button.primary", fontSize: 1, py: 3, px: 7 }}
              >
                Visa
              </button>
            </Flex>
          </Flex>
        </div>
      )}
    </div>
  )
}

DateRangePicker.propTypes = {
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  min: PropTypes.string.isRequired,
  max: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
}
